import { createReducer } from 'typesafe-actions';
import { validateVerifyPolicyLocalStorage } from '../../../helpers/validateLocalStorage';
import { updateCovieAction } from '../../actions/covie';
import {
  _resetVerifyPolicy,
  _resetVerifyPolicyInput,
  _updateComplianceProviderAction,
  _updateVerifyPolicyAdditionalData,
  _updateVerifyPolicyFlow,
  _updateVerifyPolicyInput,
} from '../../actions/verifyPolicy';
import { SharedInput } from './types';

export interface CovieState {
  carrier: {
    name: string;
  };
  inception_date: string;
}

export interface VerifyPolicyAdditionalData {
  leaseId: string | null;
  residentId: string | null;
  buildingUnitId: string | null;
}

export interface VerifyPolicyComplianceProvider {
  activeCovieLink: string | undefined | null;
  isCovieDirectLink: boolean;
  errors: {
    id: string;
    message: string;
    type: string; //TODO: create enum
  }[];
}

export interface VerifyPolicyFlow {
  hasClickedComplianceContinue: boolean;
  userFinishedComplianceFlow: boolean;
}

export interface VerifyPolicyInput extends SharedInput {
  leaseStart: string;
  leaseEnd: string;
}

export interface VerifyPolicy {
  input: VerifyPolicyInput;
  covie: CovieState;
  complianceProvider: VerifyPolicyComplianceProvider;
  flow: VerifyPolicyFlow;
  additionalData: VerifyPolicyAdditionalData;
}

const localStorageData = validateVerifyPolicyLocalStorage();

const initialState: VerifyPolicy = {
  input: {
    firstName: '',
    lastName: '',
    leaseStart: '',
    leaseEnd: '',
    address: {
      state: '',
      postalCode: '',
      municipality: '',
      street: '',
      shortNameStreet: '',
      addressNumber: '',
    },
    unit: {
      id: '',
      name: '',
    },
    addressName: '',
    email: '',
    buildingUnit: '',
    buildingId: '',
    buildingName: '',
    ...(localStorageData?.input || {}),
  },
  covie: {
    carrier: {
      name: '',
    },
    inception_date: '',
  },
  complianceProvider: {
    activeCovieLink: null,
    isCovieDirectLink: false,
    errors: [],
  },
  flow: {
    hasClickedComplianceContinue: false,
    userFinishedComplianceFlow: false,
    ...(localStorageData?.flow || {}),
  },
  additionalData: {
    leaseId: null,
    residentId: null,
    buildingUnitId: null,
    ...(localStorageData?.additionalData || {}),
  },
};

export const verifyPolicyReducer = createReducer<VerifyPolicy>(initialState)
  .handleAction(
    _updateVerifyPolicyInput,
    (
      state: VerifyPolicy,
      action: ReturnType<typeof _updateVerifyPolicyInput>,
    ): VerifyPolicy => ({
      ...state,
      input: {
        ...state.input,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    updateCovieAction,
    (
      state: VerifyPolicy,
      action: ReturnType<typeof updateCovieAction>,
    ): VerifyPolicy => ({
      ...state,
      covie: {
        ...state.covie,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    _updateComplianceProviderAction,
    (
      state: VerifyPolicy,
      action: ReturnType<typeof _updateComplianceProviderAction>,
    ): VerifyPolicy => ({
      ...state,
      complianceProvider: {
        ...state.complianceProvider,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    _updateVerifyPolicyFlow,
    (
      state: VerifyPolicy,
      action: ReturnType<typeof _updateVerifyPolicyFlow>,
    ): VerifyPolicy => ({
      ...state,
      flow: {
        ...state.flow,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    _resetVerifyPolicyInput,
    (state: VerifyPolicy): VerifyPolicy => ({
      ...state,
      input: initialState.input,
    }),
  )
  .handleAction(
    _resetVerifyPolicy,
    (): VerifyPolicy => ({
      ...initialState,
    }),
  )
  .handleAction(
    _updateVerifyPolicyAdditionalData,
    (
      state: VerifyPolicy,
      action: ReturnType<typeof _updateVerifyPolicyAdditionalData>,
    ): VerifyPolicy => ({
      ...state,
      additionalData: {
        ...state.additionalData,
        ...action.payload,
      },
    }),
  );
